import React, { useState, useEffect } from 'react';
import { getRegistrationOptions, submitRegistration } from '../services/api';

// Public registration request form. Reachable via a shareable link the
// organizer distributes, e.g.:
//   https://www.heberthoops.com/register?org=1
//
// The ?org= param identifies which organizer's tournaments/pools to show.
// If the param is missing or the organizer doesn't exist/isn't active,
// the form shows an appropriate error rather than a blank or broken state.

function Register({ onBackToLogin, orgId: orgIdProp }) {
    // Accept organizerId either as a prop (passed by App.js when it detects
    // the /register?org= deep link) or from the URL directly (fallback for
    // any other path that renders Register with the param in the URL).
    const organizerId = orgIdProp || new URLSearchParams(window.location.search).get('org');

    const [options,      setOptions]      = useState({ tournaments: [], organizer: '' });
    const [optionsError, setOptionsError] = useState('');
    const [loading,      setLoading]      = useState(true);

    const [name,         setName]         = useState('');
    const [email,        setEmail]        = useState('');
    const [tournamentId, setTournamentId] = useState('');
    const [poolIds,      setPoolIds]      = useState([]);

    const [error,        setError]        = useState('');
    const [submitting,   setSubmitting]   = useState(false);
    const [submitted,    setSubmitted]    = useState(false);

    // Load tournament/pool options on mount.
    // If no ?org= param is present, show an error immediately without hitting the API.
    useEffect(() => {
        if (!organizerId) {
            setOptionsError('Invalid registration link. Please ask your pool organizer for the correct link.');
            setLoading(false);
            return;
        }

        let cancelled = false;
        getRegistrationOptions(organizerId)
            .then(res => {
                if (cancelled) return;
                setOptions(res.data);
                // Auto-select the first tournament if there's only one --
                // saves a click for the common single-tournament case.
                if (res.data.tournaments.length === 1) {
                    setTournamentId(String(res.data.tournaments[0].id));
                }
            })
            .catch(err => {
                if (cancelled) return;
                const status = err.response?.status;
                if (status === 400 || status === 404) {
                    setOptionsError('This registration link is not valid. Please ask your pool organizer for a new one.');
                } else {
                    setOptionsError(
                        err.response?.data?.error ||
                        'Could not load registration options. Please try again later.'
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [organizerId]);

    // When tournament changes, clear pool selections so users don't
    // accidentally submit a pool from a different tournament than the one
    // currently shown.
    const handleTournamentChange = (newId) => {
        setTournamentId(newId);
        setPoolIds([]);
    };

    const togglePool = (id) => {
        setPoolIds(prev =>
            prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
        );
    };

    const selectedTournament = options.tournaments.find(
        t => String(t.id) === tournamentId
    );
    const availablePools = selectedTournament?.pools || [];

    const formValid =
        name.trim().length > 0 &&
        email.trim().length > 0 &&
        tournamentId !== '' &&
        poolIds.length > 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formValid) return;
        setError('');
        setSubmitting(true);
        try {
            await submitRegistration(
                name.trim(),
                email.trim(),
                Number(tournamentId),
                poolIds
            );
            setSubmitted(true);
        } catch (err) {
            const status = err.response?.status;
            const apiErr = err.response?.data?.error;
            if (status === 429) {
                setError('Too many requests from your network. Please try again in an hour.');
            } else if (status === 409) {
                setError(apiErr || 'A pending registration already exists for this email.');
            } else {
                setError(apiErr || 'Could not submit registration. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Render: success state ────────────────────────────────────────
    if (submitted) {
        return (
            <div className="min-h-screen bg-[#13294f] flex items-center justify-center px-4">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <div className="flex items-center justify-center gap-4 mb-4">
                            <img src="/favicon.svg" alt="" className="h-20 w-20 flex-shrink-0" />
                            <div className="text-5xl font-black text-white tracking-tight">
                                Hebert<span className="text-orange-500">Hoops</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 text-center">
                        <div className="text-green-400 text-5xl mb-4">✓</div>
                        <h2 className="text-white text-xl font-bold mb-2">Request submitted</h2>
                        <p className="text-gray-300 mb-6">
                            The pool admin will review your request shortly. If approved, you'll
                            receive an email with login instructions and a temporary password.
                        </p>
                        <button
                            onClick={onBackToLogin}
                            className="text-orange-400 hover:text-orange-300 text-sm font-medium"
                        >
                            ← Back to sign in
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ─── Render: form ────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-[#13294f] flex items-center justify-center px-4 py-8">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <img src="/favicon.svg" alt="" className="h-20 w-20 flex-shrink-0" />
                        <div className="text-5xl font-black text-white tracking-tight">
                            Hebert<span className="text-orange-500">Hoops</span>
                        </div>
                    </div>
                    <p className="text-gray-400">
                        {options.organizer
                            ? `Request access to ${options.organizer}`
                            : 'Request access to the pool'}
                    </p>
                </div>
                <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    {loading && (
                        <div className="text-gray-400 text-center py-8">Loading...</div>
                    )}

                    {!loading && optionsError && (
                        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg text-sm">
                            {optionsError}
                        </div>
                    )}

                    {!loading && !optionsError && options.tournaments.length === 0 && (
                        <div className="text-gray-400 text-center py-4">
                            No tournaments are currently accepting registrations.
                        </div>
                    )}

                    {!loading && !optionsError && options.tournaments.length > 0 && (
                        <>
                            {error && (
                                <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                                    {error}
                                </div>
                            )}
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Your name
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                        placeholder="Aunt Burgundi"
                                        data-testid="name-input"
                                        autoComplete="name"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                        placeholder="you@example.com"
                                        autoCorrect="off"
                                        autoCapitalize="none"
                                        autoComplete="email"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Tournament
                                    </label>
                                    <select
                                        value={tournamentId}
                                        onChange={e => handleTournamentChange(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
                                        required
                                    >
                                        <option value="">Select a tournament...</option>
                                        {options.tournaments.map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.name} ({t.year})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {tournamentId && availablePools.length === 0 && (
                                    <div className="text-gray-400 text-sm">
                                        This tournament has no pools currently open for registration.
                                    </div>
                                )}

                                {tournamentId && availablePools.length > 0 && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Pools
                                        </label>
                                        <div className="space-y-2">
                                            {availablePools.map(pool => (
                                                <label
                                                    key={pool.id}
                                                    className="flex items-center gap-3 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 cursor-pointer hover:border-gray-600"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={poolIds.includes(pool.id)}
                                                        onChange={() => togglePool(pool.id)}
                                                        className="w-4 h-4 accent-orange-500"
                                                    />
                                                    <span className="text-white">{pool.name}</span>
                                                    <span className="text-gray-500 text-xs ml-auto">
                                                        {pool.pool_type === 'bracket'
                                                            ? 'Bracket'
                                                            : 'Round-by-Round'}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={!formValid || submitting}
                                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-colors"
                                >
                                    {submitting ? 'Submitting...' : 'Request access'}
                                </button>
                            </form>
                        </>
                    )}

                    <div className="text-center mt-6">
                        <button
                            onClick={onBackToLogin}
                            className="text-gray-400 hover:text-gray-300 text-sm"
                        >
                            ← Back to sign in
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Register;